import React, { useState, useMemo, useEffect } from 'react';
import { usePackagingOperationsContext } from '../context/PackagingOperationsContext';
import { pkgOperationsService } from '../services/pkgOperationsService';
import { PKG_PRODUCTS_SEED_DATA } from '../constants/pkgProductsSeed';

type PqiSubEval = 'NetWeight' | 'Product' | 'Primary' | 'Secondary' | 'CBB';

interface ProductItem {
    title: string;
    productCode: string;
    lineName: string;
    productCategory: string;
}

// Helper to extract standard weight from product title or SKU
const extractWeightFromText = (text: string): number => {
    if (!text) return 150;
    // Look for patterns like "75GM", "75G", "75+25G", "125GM", "1KG", "250G", "414G"
    const matchKg = text.match(/(\d+(?:\.\d+)?)\s*kg/i);
    if (matchKg) {
        return parseFloat(matchKg[1]) * 1000;
    }
    const matchCombo = text.match(/(\d+)\s*\+\s*(\d+)\s*g/i);
    if (matchCombo) {
        return parseFloat(matchCombo[1]) + parseFloat(matchCombo[2]);
    }
    const matchG = text.match(/(\d+(?:\.\d+)?)\s*(?:gm|g)\b/i);
    if (matchG) {
        return parseFloat(matchG[1]);
    }
    const numOnly = parseFloat(text.replace(/[^0-9.]/g, ''));
    if (!isNaN(numOnly) && numOnly > 0 && numOnly <= 2000) {
        return numOnly;
    }
    return 150;
};

const PQI: React.FC = () => {
    const { state, dispatch } = usePackagingOperationsContext();

    // PQI Sub-Evaluation navigation
    const [activeSubEval, setActiveSubEval] = useState<PqiSubEval>('NetWeight');

    // Status tracking across all 5 sub-evaluations
    const [status, setStatus] = useState<Record<PqiSubEval, 'Pending' | 'Saved'>>({
        NetWeight: 'Pending',
        Product: 'Pending',
        Primary: 'Pending',
        Secondary: 'Pending',
        CBB: 'Pending'
    });

    // ─── Universal Product & Variety Setup (All 1,105 Varieties) ───
    const [selectedCategory, setSelectedCategory] = useState<string>('All Categories');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [productName, setProductName] = useState<string>('');
    const [sku, setSku] = useState<string>('');
    const [pkd, setPkd] = useState<string>(new Date().toISOString().slice(0, 10));
    const [batchCode, setBatchCode] = useState<string>('');

    // Extract unique categories from seed data
    const categoriesList = useMemo(() => {
        const catSet = new Set<string>();
        PKG_PRODUCTS_SEED_DATA.forEach(p => {
            if (p.productCategory && p.productCategory.trim()) {
                catSet.add(p.productCategory.trim());
            }
        });
        return ['All Categories', ...Array.from(catSet).sort((a, b) => a.localeCompare(b))];
    }, []);

    // Filter products by active line, category, and search query
    const filteredProducts = useMemo(() => {
        const activeLine = state.sessionData.lineNo || '';
        let list: ProductItem[] = PKG_PRODUCTS_SEED_DATA;

        // Filter by line if applicable, unless "All Lines" or no specific line match
        if (activeLine && activeLine !== 'All Lines') {
            const lineNumMatch = activeLine.match(/\d+/);
            const lineNum = lineNumMatch ? lineNumMatch[0] : '';
            const lineFiltered = list.filter(p => {
                if (!p.lineName || p.lineName.toLowerCase().includes('all')) return true;
                return p.lineName.includes(lineNum) || p.lineName.toLowerCase() === activeLine.toLowerCase();
            });
            if (lineFiltered.length > 0) {
                list = lineFiltered;
            }
        }

        // Filter by category
        if (selectedCategory && selectedCategory !== 'All Categories') {
            list = list.filter(p => p.productCategory.toLowerCase() === selectedCategory.toLowerCase());
        }

        // Filter by search query
        if (searchTerm.trim()) {
            const query = searchTerm.toLowerCase().trim();
            list = list.filter(p => 
                p.title.toLowerCase().includes(query) || 
                (p.productCode && p.productCode.toLowerCase().includes(query))
            );
        }

        return list;
    }, [selectedCategory, searchTerm, state.sessionData.lineNo]);

    // Handle product selection & auto-fill SKU + Standard Weight
    const handleProductSelect = (selectedTitle: string) => {
        setProductName(selectedTitle);
        const matched = PKG_PRODUCTS_SEED_DATA.find(p => p.title === selectedTitle);
        if (matched) {
            const code = matched.productCode || '';
            setSku(code);
            if (matched.productCategory && selectedCategory === 'All Categories') {
                setSelectedCategory(matched.productCategory);
            }
            // Auto calculate standard weight from product title or code
            const detectedWeight = extractWeightFromText(selectedTitle) || extractWeightFromText(code);
            setStandardWeight(detectedWeight);
        } else {
            setSku(selectedTitle);
            const detectedWeight = extractWeightFromText(selectedTitle);
            setStandardWeight(detectedWeight);
        }
    };

    // Auto-select first product if empty
    useEffect(() => {
        if (!productName && filteredProducts.length > 0) {
            handleProductSelect(filteredProducts[0].title);
        }
    }, [filteredProducts]);

    // ─── 1. Net Weight Evaluation State & Calculations ───
    const [standardWeight, setStandardWeight] = useState<number>(150);
    const [nwSamples, setNwSamples] = useState<string[]>(Array(15).fill(''));

    // Rectified Net Weight Metrics:
    // 1. Average = sum of valid entries / count
    // 2. Give-Away = (avg > standardWeight) ? (avg - standardWeight) : 0  (GIVE-AWAY IS EXTRA PRODUCT GIVEN FREE WHEN OVERWEIGHT)
    // 3. Shortfall = (standardWeight > avg) ? (standardWeight - avg) : 0
    const netWeightMetrics = useMemo(() => {
        const valid = nwSamples
            .map(v => parseFloat(v))
            .filter(v => !isNaN(v) && v > 0);

        if (valid.length === 0) {
            return {
                validCount: 0,
                avg: 0,
                avgStr: '-',
                giveAway: 0,
                giveAwayStr: '-',
                shortfall: 0,
                shortfallStr: '-',
                isUnderweight: false,
                isOptimal: true
            };
        }

        const sum = valid.reduce((acc, curr) => acc + curr, 0);
        const avg = sum / valid.length;
        const std = standardWeight > 0 ? standardWeight : 150;

        // Correct give-away: actual weight exceeding standard weight
        const giveAway = avg > std ? (avg - std) : 0;
        // Shortfall: actual weight under standard weight
        const shortfall = std > avg ? (std - avg) : 0;

        return {
            validCount: valid.length,
            avg: Number(avg.toFixed(2)),
            avgStr: `${avg.toFixed(2)} g`,
            giveAway: Number(giveAway.toFixed(2)),
            giveAwayStr: `${giveAway.toFixed(2)} g`,
            shortfall: Number(shortfall.toFixed(2)),
            shortfallStr: `${shortfall.toFixed(2)} g`,
            isUnderweight: shortfall > 0,
            isOptimal: shortfall === 0
        };
    }, [nwSamples, standardWeight]);

    // ─── 2-5. Evaluations State (Product, Primary, Secondary, CBB) ───
    const createInitialSamples = () => Array.from({ length: 10 }).map((_, i) => ({
        id: i, status: 'Okay', category: '', detail: ''
    }));

    const [productSamples, setProductSamples] = useState(createInitialSamples());
    const [primarySamples, setPrimarySamples] = useState(createInitialSamples());
    const [secondarySamples, setSecondarySamples] = useState(createInitialSamples());
    const [cbbSamples, setCbbSamples] = useState(createInitialSamples());

    const activeEvalState = useMemo(() => {
        switch (activeSubEval) {
            case 'Product': return { samples: productSamples, setSamples: setProductSamples };
            case 'Primary': return { samples: primarySamples, setSamples: setPrimarySamples };
            case 'Secondary': return { samples: secondarySamples, setSamples: setSecondarySamples };
            case 'CBB': return { samples: cbbSamples, setSamples: setCbbSamples };
            default: return null;
        }
    }, [activeSubEval, productSamples, primarySamples, secondarySamples, cbbSamples]);

    // Calculate defect rate for pack evaluations
    const activeEvalDefectRate = useMemo(() => {
        if (!activeEvalState) return { defective: 0, total: 10, pctStr: '0.00%', hasDefects: false };
        const defective = activeEvalState.samples.filter(s => s.status === 'Not Okay').length;
        const pct = (defective / 10) * 100;
        return {
            defective,
            total: 10,
            pctStr: `${pct.toFixed(2)}%`,
            hasDefects: defective > 0
        };
    }, [activeEvalState]);

    // ─── Save Component Action ───
    const handleSaveComponent = async () => {
        if (!productName) {
            alert('Please select a Product Variety first.');
            return;
        }

        const tourId = state.sessionData.tourId || `QT-${Date.now()}`;
        const userName = state.sessionData.qaExecutive || null;

        if (activeSubEval === 'NetWeight') {
            // Validate net weight entries
            const validWeights = nwSamples.map(v => parseFloat(v)).filter(v => !isNaN(v) && v > 0);
            if (validWeights.length < 15) {
                const proceed = window.confirm(`Only ${validWeights.length} of 15 sample weights have been entered. Do you still want to save?`);
                if (!proceed) return;
            }

            try {
                await pkgOperationsService.savePqiNetWeightData({
                    tourId,
                    productName,
                    sku: sku || productName,
                    standardWeight: standardWeight || 150,
                    averageWeight: netWeightMetrics.avg || 0,
                    giveAway: netWeightMetrics.giveAway || 0,
                    sampleWeights: validWeights,
                    userName
                });
            } catch (err) {
                console.warn('Error saving Net Weight to Dataverse:', err);
            }

            setStatus(prev => ({ ...prev, NetWeight: 'Saved' }));
            alert('Net Weight evaluation successfully recorded and saved!');
        } else if (activeEvalState) {
            // Save Product / Primary / Secondary / CBB evaluation
            try {
                await pkgOperationsService.savePqiEvaluationData({
                    tourId,
                    evaluationType: activeSubEval,
                    productName,
                    sku: sku || productName,
                    pkd,
                    batchCode,
                    samples: activeEvalState.samples,
                    userName
                });
            } catch (err) {
                console.warn(`Error saving ${activeSubEval} evaluation to Dataverse:`, err);
            }

            setStatus(prev => ({ ...prev, [activeSubEval]: 'Saved' }));
            alert(`${activeSubEval} evaluation component successfully saved!`);
        }
    };

    // ─── Sync with Global Packaging Operations Context ───
    useEffect(() => {
        dispatch({
            type: 'SET_CHECKLIST_DATA',
            payload: {
                productName,
                sku,
                pkd,
                batchCode,
                // Net weight data
                standardWeight,
                nwSamples,
                averageWeight: netWeightMetrics.avg,
                avgWeight: netWeightMetrics.avgStr,
                giveAway: netWeightMetrics.giveAway,
                giveAwayStr: netWeightMetrics.giveAwayStr,
                shortfall: netWeightMetrics.shortfall,
                isUnderweight: netWeightMetrics.isUnderweight,
                // Evaluations data
                productSamples,
                primarySamples,
                secondarySamples,
                cbbSamples,
                subStatus: status
            }
        });
    }, [
        productName, sku, pkd, batchCode,
        standardWeight, nwSamples, netWeightMetrics,
        productSamples, primarySamples, secondarySamples, cbbSamples,
        status, dispatch
    ]);

    return (
        <div style={{ padding: '20px', fontFamily: "'Outfit', sans-serif, system-ui", color: '#1e293b' }}>
            {/* Header: Title & Info */}
            <div style={{ borderBottom: '2px solid #e2e8f0', paddingBottom: '12px', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 800, margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ display: 'inline-block', width: '4px', height: '18px', backgroundColor: '#4f46e5', borderRadius: '2px' }}></span>
                    PQI (Product Quality Index) Evaluation
                </h3>
                <p style={{ margin: '4px 0 0 12px', fontSize: '13px', color: '#64748b' }}>
                    Inspect net weight consistency across 15 packs and evaluate Product, Primary, Secondary, and CBB quality.
                </p>
            </div>

            {/* Universal Product & Variety Setup Card (All 1,105 Varieties Included) */}
            <div style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '16px', marginBottom: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#4338ca', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Product Variety & Packaging Identification</span>
                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                        {filteredProducts.length} Varieties Available
                    </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                    {/* Category Filter */}
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
                            Category Filter
                        </label>
                        <select
                            style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: 600, background: '#f8fafc', outline: 'none' }}
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            {categoriesList.map((cat, i) => (
                                <option key={i} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>

                    {/* Product Variety Selector (1,105 Varieties) */}
                    <div style={{ gridColumn: 'span 2' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                                Product Variety <span style={{ color: '#dc2626' }}>*</span>
                            </label>
                            {filteredProducts.length > 20 && (
                                <input
                                    type="text"
                                    placeholder="Quick search variety..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ fontSize: '11px', padding: '2px 6px', border: '1px solid #cbd5e1', borderRadius: '4px', outline: 'none', width: '160px' }}
                                />
                            )}
                        </div>
                        <select
                            style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: 700, color: '#1e293b', background: '#ffffff', outline: 'none' }}
                            value={productName}
                            onChange={(e) => handleProductSelect(e.target.value)}
                        >
                            {filteredProducts.map((p, idx) => (
                                <option key={idx} value={p.title}>
                                    {p.title} {p.productCode ? `(${p.productCode})` : ''} - {p.lineName}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* SKU / Code */}
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
                            SKU / Product Code
                        </label>
                        <input
                            type="text"
                            style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', fontWeight: 600, color: '#64748b', background: '#f8fafc', outline: 'none' }}
                            value={sku}
                            onChange={(e) => setSku(e.target.value)}
                            placeholder="SKU"
                        />
                    </div>

                    {/* PKD Date */}
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
                            PKD Date
                        </label>
                        <input
                            type="date"
                            style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: 600, color: '#1e293b', background: '#ffffff', outline: 'none' }}
                            value={pkd}
                            onChange={(e) => setPkd(e.target.value)}
                        />
                    </div>

                    {/* Batch Code */}
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', marginBottom: '4px', textTransform: 'uppercase' }}>
                            Batch Code
                        </label>
                        <input
                            type="text"
                            style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: 600, color: '#1e293b', background: '#ffffff', outline: 'none' }}
                            value={batchCode}
                            onChange={(e) => setBatchCode(e.target.value)}
                            placeholder="e.g. B26-10"
                        />
                    </div>
                </div>
            </div>

            {/* Sub-Evaluation Switcher with Live Saved / Pending Badges */}
            <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '14px 16px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ minWidth: '260px', flex: '1' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Select Sub-Evaluation Section:
                        </label>
                        <select
                            style={{ width: '100%', maxWidth: '340px', padding: '8px 12px', borderRadius: '6px', border: '2px solid #4f46e5', fontSize: '14px', fontWeight: 700, color: '#1e293b', background: '#ffffff', outline: 'none' }}
                            value={activeSubEval}
                            onChange={(e) => setActiveSubEval(e.target.value as PqiSubEval)}
                        >
                            <option value="NetWeight">1. Net Weight Evaluation (15 Samples)</option>
                            <option value="Product">2. Product Evaluation (10 Samples)</option>
                            <option value="Primary">3. Primary Pack Evaluation (10 Samples)</option>
                            <option value="Secondary">4. Secondary Pack Evaluation (10 Samples)</option>
                            <option value="CBB">5. CBB Evaluation (10 Samples)</option>
                        </select>
                    </div>

                    {/* Status Badges */}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {(['NetWeight', 'Product', 'Primary', 'Secondary', 'CBB'] as PqiSubEval[]).map(key => {
                            const isSaved = status[key] === 'Saved';
                            const label = key === 'NetWeight' ? 'Net Weight' : key;
                            return (
                                <span
                                    key={key}
                                    style={{
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        border: isSaved ? '1px solid #86efac' : '1px solid #cbd5e1',
                                        backgroundColor: isSaved ? '#dcfce7' : '#f1f5f9',
                                        color: isSaved ? '#15803d' : '#64748b',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: isSaved ? '#16a34a' : '#94a3b8' }}></span>
                                    {label}: {status[key]}
                                </span>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ─── Sub-Evaluation Form Container ─── */}
            <div style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                {activeSubEval === 'NetWeight' ? (
                    /* 1. Net Weight Evaluation */
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#1e293b' }}>
                                    ⚖️ 1. Net Weight Inspection (15 Samples)
                                </h4>
                                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                                    Weigh 15 packs in grams. Average weight and Give-Away are calculated in real-time.
                                </p>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                                    Standard Weight (g):
                                </label>
                                <input
                                    type="number"
                                    step="0.1"
                                    min={1}
                                    style={{ width: '110px', padding: '6px 10px', borderRadius: '6px', border: '2px solid #4f46e5', fontSize: '14px', fontWeight: 800, color: '#1e293b', textAlign: 'center', outline: 'none' }}
                                    value={standardWeight}
                                    onChange={(e) => setStandardWeight(parseFloat(e.target.value) || 0)}
                                />
                            </div>
                        </div>

                        {/* 15 Weight Inputs Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                            {nwSamples.map((w, idx) => {
                                const numVal = parseFloat(w);
                                const isFilled = !isNaN(numVal) && numVal > 0;
                                const isUnder = isFilled && standardWeight > 0 && numVal < standardWeight;

                                return (
                                    <div
                                        key={idx}
                                        style={{
                                            background: isUnder ? '#fff1f2' : '#f8fafc',
                                            border: isUnder ? '1px solid #fecdd3' : '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            padding: '8px 10px'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 700, color: isUnder ? '#e11d48' : '#64748b', marginBottom: '4px' }}>
                                            <span>Sample {idx + 1}</span>
                                            {isUnder && <span>⚠ Low</span>}
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="Weight"
                                            style={{
                                                width: '100%',
                                                padding: '6px 8px',
                                                borderRadius: '4px',
                                                border: '1px solid #cbd5e1',
                                                fontSize: '13px',
                                                fontWeight: 700,
                                                textAlign: 'center',
                                                color: '#0f172a',
                                                outline: 'none',
                                                background: '#ffffff'
                                            }}
                                            value={w}
                                            onChange={(e) => {
                                                const copy = [...nwSamples];
                                                copy[idx] = e.target.value;
                                                setNwSamples(copy);
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        {/* Corrected Real-Time KPI Cards (Average Weight & Correct Give-Away) */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '16px' }}>
                            <div style={{ textAlign: 'center', borderRight: '1px solid #e2e8f0', paddingRight: '12px' }}>
                                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                                    Standard Declared Weight
                                </span>
                                <div style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', marginTop: '4px' }}>
                                    {standardWeight} g
                                </div>
                                <span style={{ fontSize: '11px', color: '#64748b' }}>
                                    ({netWeightMetrics.validCount} of 15 weighed)
                                </span>
                            </div>

                            <div style={{ textAlign: 'center', borderRight: '1px solid #e2e8f0', paddingRight: '12px' }}>
                                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                                    Observed Average Weight
                                </span>
                                <div style={{ fontSize: '20px', fontWeight: 800, color: netWeightMetrics.isUnderweight ? '#dc2626' : '#16a34a', marginTop: '4px' }}>
                                    {netWeightMetrics.avgStr}
                                </div>
                                <span style={{ fontSize: '11px', color: netWeightMetrics.isUnderweight ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                                    {netWeightMetrics.isUnderweight ? `Shortfall: ${netWeightMetrics.shortfallStr}` : 'Meets Standard'}
                                </span>
                            </div>

                            <div style={{ textAlign: 'center' }}>
                                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                                    Give-Away (Excess Product Over Std)
                                </span>
                                <div style={{ fontSize: '20px', fontWeight: 800, color: netWeightMetrics.giveAway > 0 ? '#d97706' : '#10b981', marginTop: '4px' }}>
                                    {netWeightMetrics.giveAwayStr}
                                </div>
                                <span style={{ fontSize: '11px', color: '#64748b' }}>
                                    {netWeightMetrics.giveAway > 0 ? 'Cost give-away to consumers' : '0.00 g (Strictly Compliant)'}
                                </span>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* 2, 3, 4, 5: Pack Evaluations (Product, Primary, Secondary, CBB) */
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#1e293b' }}>
                                    📦 {activeSubEval} Evaluation (10 Samples)
                                </h4>
                                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                                    Mark each sample as Okay or Not Okay. If Not Okay, specify defect category and details.
                                </p>
                            </div>

                            {/* Defect Rate Indicator */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: activeEvalDefectRate.hasDefects ? '#dc2626' : '#16a34a' }}>
                                    {activeEvalDefectRate.defective} of 10 Defective ({activeEvalDefectRate.pctStr})
                                </span>
                                <span style={{
                                    fontSize: '11px',
                                    fontWeight: 800,
                                    padding: '3px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: activeEvalDefectRate.hasDefects ? '#fee2e2' : '#dcfce7',
                                    color: activeEvalDefectRate.hasDefects ? '#dc2626' : '#15803d'
                                }}>
                                    {activeEvalDefectRate.hasDefects ? 'DEFECT DETECTED' : 'OPTIMAL'}
                                </span>
                            </div>
                        </div>

                        {/* Samples Table */}
                        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        <th style={{ padding: '10px 12px', width: '10%', textAlign: 'center', fontWeight: 700, color: '#475569' }}>Sample</th>
                                        <th style={{ padding: '10px 12px', width: '18%', fontWeight: 700, color: '#475569' }}>Status</th>
                                        <th style={{ padding: '10px 12px', width: '32%', fontWeight: 700, color: '#475569' }}>Defect Category</th>
                                        <th style={{ padding: '10px 12px', width: '40%', fontWeight: 700, color: '#475569' }}>Defect Detail / Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activeEvalState && activeEvalState.samples.map((sample, idx) => {
                                        const isNotOk = sample.status === 'Not Okay';
                                        return (
                                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: isNotOk ? '#fff1f2' : (idx % 2 === 0 ? '#ffffff' : '#fcfcfc') }}>
                                                <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: isNotOk ? '#dc2626' : '#64748b' }}>
                                                    Sample {idx + 1}
                                                </td>
                                                <td style={{ padding: '8px 12px' }}>
                                                    <select
                                                        style={{
                                                            width: '100%',
                                                            padding: '6px 10px',
                                                            borderRadius: '6px',
                                                            border: isNotOk ? '1px solid #fca5a5' : '1px solid #cbd5e1',
                                                            fontSize: '13px',
                                                            fontWeight: 700,
                                                            color: isNotOk ? '#dc2626' : '#16a34a',
                                                            backgroundColor: isNotOk ? '#fef2f2' : '#ffffff',
                                                            outline: 'none'
                                                        }}
                                                        value={sample.status}
                                                        onChange={(e) => {
                                                            const newStatus = e.target.value;
                                                            activeEvalState.setSamples(prev => prev.map((s, i) => i === idx ? {
                                                                ...s,
                                                                status: newStatus,
                                                                category: newStatus === 'Okay' ? '' : s.category,
                                                                detail: newStatus === 'Okay' ? '' : s.detail
                                                            } : s));
                                                        }}
                                                    >
                                                        <option value="Okay">Okay</option>
                                                        <option value="Not Okay">Not Okay</option>
                                                    </select>
                                                </td>
                                                <td style={{ padding: '8px 12px' }}>
                                                    <input
                                                        type="text"
                                                        placeholder={isNotOk ? 'Required: Enter defect category...' : 'Category (disabled for Okay)'}
                                                        disabled={!isNotOk}
                                                        style={{
                                                            width: '100%',
                                                            padding: '6px 10px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #cbd5e1',
                                                            fontSize: '13px',
                                                            backgroundColor: isNotOk ? '#ffffff' : '#f1f5f9',
                                                            color: isNotOk ? '#0f172a' : '#94a3b8',
                                                            outline: 'none'
                                                        }}
                                                        value={sample.category}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            activeEvalState.setSamples(prev => prev.map((s, i) => i === idx ? { ...s, category: val } : s));
                                                        }}
                                                    />
                                                </td>
                                                <td style={{ padding: '8px 12px' }}>
                                                    <input
                                                        type="text"
                                                        placeholder={isNotOk ? 'Enter defect detail...' : 'Detail (disabled for Okay)'}
                                                        disabled={!isNotOk}
                                                        style={{
                                                            width: '100%',
                                                            padding: '6px 10px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #cbd5e1',
                                                            fontSize: '13px',
                                                            backgroundColor: isNotOk ? '#ffffff' : '#f1f5f9',
                                                            color: isNotOk ? '#0f172a' : '#94a3b8',
                                                            outline: 'none'
                                                        }}
                                                        value={sample.detail}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            activeEvalState.setSamples(prev => prev.map((s, i) => i === idx ? { ...s, detail: val } : s));
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Save Evaluation Component Button */}
                <div style={{ textAlign: 'right', marginTop: '20px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                    <button
                        type="button"
                        onClick={handleSaveComponent}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#4f46e5',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '9px 18px',
                            fontSize: '13px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(79,70,229,0.25)',
                            transition: 'all 0.15s ease'
                        }}
                    >
                        <span>Save {activeSubEval === 'NetWeight' ? 'Net Weight' : activeSubEval} Component</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PQI;
